import {
  AuthContext,
  capitalize,
  useApi,
  Payment,
  CommonContext,
} from '@repo-level/core-front';
import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute, useNavigation } from 'react-navi';

import { Button, ButtonCross } from 'components/buttons';
import { fetchPaymentMethods } from 'helpers/payments';
import { ModalDefaultProps } from 'components/Modal';
import { UserWallets } from 'components/general/Header/UserWallets';
import { Spinner } from 'components/Spinner';
import { groupMemberBR, groupMemberLA, groupMemberNoKYC } from 'helpers/user';
import { FullInformation } from 'components/auth/FullInformation';
import { Registration } from 'components/auth/Registration';
import { ModalsContext, ModalType } from 'contexts/modal';
import { removeQueryParameterWithNavigation } from 'helpers/common';
import { AddonsContext } from 'contexts/addons';
import { checkCompleteUserInformation } from 'components/Deposit/helpers';
import { PaymentMethod } from 'components/Deposit/PaymentMethod';
import { Country } from 'helpers/conf';
import { AnalyticsEventName } from 'api/analytics';
import { useAnalytics } from 'helpers/analytics';
import { TooltipMessage } from 'components/tooltip';
import { TooltipTypes } from 'modules/globalTooltip';

import {
  ScCurrencyUser,
  ScWithdrawContent,
  ScWithdrawModal,
  ScWithdrawNav,
  ScWithdrawRow,
  ScWithdrawTitle,
  ScNotFound,
  ScPaymentsMethods,
} from './styled';

export const WithdrawModal = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose, onScrollTo }, ref) => {
    const { user, pendingUser } = useContext(AuthContext);
    const { currentIp, currencies } = useContext(CommonContext);
    const { depositBonuses } = useContext(AddonsContext);

    const isBonusRestricted = useMemo(() => groupMemberBR(user), [user]);

    const navigation = useNavigation();
    const {
      url: { query },
    } = useCurrentRoute();

    const removeVipQuery = useCallback(() => {
      if (query.vip) {
        removeQueryParameterWithNavigation(navigation, query, 'vip');
      }
    }, [navigation, query]);

    const handleClose = useCallback(() => {
      removeVipQuery();

      onClose && onClose();
    }, [onClose, removeVipQuery]);

    const handleCloseModal = useCallback(() => {
      removeVipQuery();

      if (
        !user?.canIssueBonuses ||
        isBonusRestricted ||
        groupMemberLA(user) ||
        depositBonuses.length === 0
      )
        return onClose && onClose();

      onClose && onClose();
    }, [
      depositBonuses.length,
      isBonusRestricted,
      onClose,
      removeVipQuery,
      user,
    ]);

    if (user?.id == null && !pendingUser) {
      return <Registration onClose={handleClose} ref={ref} />;
    }

    if (
      (!checkCompleteUserInformation(
        user,
        currentIp?.countryCode === Country.France
      ) ||
        user?.state === null) &&
      currencies.find(({ code }) => code === user?.currency)?.fiat
    ) {
      return <FullInformation onClose={handleClose} ref={ref} />;
    }

    return (
      <ScWithdrawModal ref={ref}>
        <ButtonCross top={8} right={8} onClick={handleCloseModal} />
        <WithdrawComponent onScrollTo={onScrollTo} onClose={handleCloseModal} />
      </ScWithdrawModal>
    );
  }
);

const WithdrawComponent: FC<ModalDefaultProps> = ({ onScrollTo, onClose }) => {
  const { t } = useTranslation(['cashbox', 'deposits', 'verification']);
  const { navigate } = useNavigation();

  const refDepositForm = useRef<HTMLDivElement>(null);

  const { user } = useContext(AuthContext);

  const { sendEventToAnalytics } = useAnalytics();

  const [
    currentPaymentMethod,
    setCurrentPaymentMethod,
  ] = useState<Payment.Method>();

  const showVerificationMessage = useMemo(
    () =>
      currentPaymentMethod?.type !== Payment.MethodType.Crypto &&
      groupMemberNoKYC(user),
    [currentPaymentMethod, user]
  );

  const [
    requestPaymentMethods,
    { data: paymentMethods, pending: pendingPaymentMethods },
  ] = useApi(fetchPaymentMethods, undefined);

  useEffect(() => {
    if (user?.currency == null) return;
    requestPaymentMethods(user.currency, Payment.Action.Cashout);
  }, [requestPaymentMethods, user]);

  useEffect(() => {
    if (pendingPaymentMethods || paymentMethods == null) return;

    setCurrentPaymentMethod(paymentMethods[0]);
  }, [setCurrentPaymentMethod, paymentMethods, pendingPaymentMethods]);

  const handleSelectPaymentMethods = useCallback(
    (method: Payment.Method) => {
      setCurrentPaymentMethod(method);

      onScrollTo && onScrollTo(refDepositForm);
    },
    [onScrollTo]
  );

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__WITHDRAW__FORM__APPEARANCE,
      attributes: {
        additionalData: JSON.stringify({ from: window.location.href }),
      },
    });
  }, [sendEventToAnalytics]);

  if (user?.currency == null) {
    return null;
  }

  return (
    <>
      <ScWithdrawTitle>{t('cashbox:withdraw')}</ScWithdrawTitle>
      <ScWithdrawRow>
        <ScWithdrawNav>
          <CurrencyUser />
          <PaymentsMethods
            title={t('deposits:allPaymentMethod')}
            currentMethod={currentPaymentMethod}
            methods={paymentMethods}
            pending={pendingPaymentMethods}
            onSelect={handleSelectPaymentMethods}
          />
        </ScWithdrawNav>
        <ScWithdrawContent ref={refDepositForm}>
          {pendingPaymentMethods ? (
            <Spinner size={64} center />
          ) : currentPaymentMethod == null ? (
            <ScNotFound>
              {capitalize(t('deposits:notSelectedMethod'))}
            </ScNotFound>
          ) : (
            <>
              {showVerificationMessage && (
                <TooltipMessage
                  type={TooltipTypes.WARNING}
                  text={t('verification:verifyNotification')}
                  withArrow
                  onClick={() => {
                    navigate('/profile-custom/verification');
                    onClose && onClose();
                  }}
                  marginBottom={24}
                />
              )}

              <PaymentMethod
                userCurrency={user.currency}
                paymentMethod={currentPaymentMethod}
                paymentAction={Payment.Action.Cashout}
              />
            </>
          )}
        </ScWithdrawContent>
      </ScWithdrawRow>
    </>
  );
};

const CurrencyUser = () => {
  const { t } = useTranslation();

  const { handleModal } = useContext(ModalsContext);

  return (
    <ScCurrencyUser>
      <h3>{t('glossary:currency')}</h3>
      <div className="row">
        <div className="selector">
          <UserWallets
            hideDepositButton
            darkSelector
            disableSelectionForOneCurrency
          />
        </div>
        <Button
          type="button"
          whiteTransparent
          medium
          wide
          onClick={handleModal(ModalType.EditCurrencies, {
            stayInSameWindow: true,
          })}
        >
          {t('glossary:addCurrency')}
        </Button>
      </div>
    </ScCurrencyUser>
  );
};

interface PaymentsMethodsProps {
  title: string;
  currentMethod: Payment.Method | undefined;
  methods: Payment.Method[] | undefined;
  pending: boolean;
  onSelect: (method: Payment.Method) => void;
}

const PaymentsMethods: FC<PaymentsMethodsProps> = ({
  title,
  currentMethod,
  methods,
  pending,
  onSelect,
}) => {
  const { t } = useTranslation('deposits');

  return (
    <ScPaymentsMethods>
      <h3>{capitalize(title)}</h3>
      {pending ? (
        <Spinner size={64} center />
      ) : methods == null || methods.length === 0 ? (
        <ScNotFound>{t('notFoundPaymentsMethods')}</ScNotFound>
      ) : (
        <div className="payments">
          {methods.map((method, index) => (
            <button
              key={`${method.id}_${index}`}
              onClick={() => onSelect(method)}
              className={method.id === currentMethod?.id ? 'active' : ''}
            >
              <img
                src={method.iconSrc || undefined}
                alt={`${method.translationKey} logo`}
              />
            </button>
          ))}
        </div>
      )}
    </ScPaymentsMethods>
  );
};
